import _ from 'lodash-es'
import gsap from 'gsap'
import { FullGestureState } from 'react-use-gesture/dist/types'
import { useRef, useCallback } from 'react'
import { ANIMATION_DURATION } from './constants'

type ScrollState = ReturnType<typeof buildScrollState>
type PositionGetter = (state: ScrollState, ...args: any[]) => number
type PositionGetterParams<T extends PositionGetter> = T extends (
  state: any,
  ...args: infer P
) => any
  ? P
  : never

interface ScrollConfig<T extends PositionGetter> {
  onStart: () => void
  onComplete: () => void
  to: T
}

export function useScrollState<T extends PositionGetter>({
  onStart,
  onComplete,
  to,
}: ScrollConfig<T>) {
  const scrollState = useRef(buildScrollState()).current

  const scrollTimeline = useRef<gsap.core.Timeline>()

  const stopScrollAnimation = useCallback(() => {
    scrollTimeline.current?.pause()

    if (scrollTimeline.current?.progress() < 1) onComplete()

    scrollTimeline.current = null
  }, [])

  const scrollTo = useCallback((...args: PositionGetterParams<T>) => {
    stopScrollAnimation()

    const duration = ANIMATION_DURATION
    const current = to(scrollState, ...args)
    const ease = Math.abs(current - scrollState.current) > 100 ? 'circ.out' : 'power1.out'

    scrollTimeline.current = gsap
      .timeline({ onStart, onComplete })
      .to(scrollState, { current, duration, ease })

    return scrollTimeline.current
  }, [])

  return [scrollState, scrollTo, stopScrollAnimation] as const
}

const buildScrollState = () => {
  return {
    current: 0,
    offset: 0, // distance between dragStart and drag
    speed: 0, // distanche between consecutive drags
    dragCount: 0,
    isDragging: false,
    isTouch: false,
    get total(): number {
      return this.offset + this.current
    },

    updateOnDrag(gestureState: FullGestureState<'drag'>) {
      const {
        dragging,
        xy: [x],
        offset: [offsetX],
        previous: [prevX],
        lastOffset: [startX],
      } = gestureState

      if (dragging) this.dragCount++
      if (dragging && this.dragCount > 1) this.speed = prevX - x

      this.offset = startX - offsetX
    },

    updateOnDragEnd() {
      this.current += this.offset
      this.offset = 0
      this.speed = 0
      this.isDragging = false

      if (this.isTouch) this.resetDragCount()
    },

    updateOnDragStart() {
      this.isDragging = true
    },
    updateOnTouchStart() {
      this.isTouch = true
    },

    get hasDragged() {
      return this.dragCount > 1
    },

    resetDragCount() {
      this.dragCount = 0
    },
  }
}
